import { useCallback } from "react";
import shallow from "zustand/shallow";
import { useStats } from "../context/stats";
import useCurrency, { CurrencyKey } from "./useCurrency";

export const useHntMarket = () => {
  const { currentCurrency, getCurrencyTitle } = useCurrency(
    (state) => ({
      currentCurrency: state.currency,
      getCurrencyTitle: state.getCurrencyTitle
    }),
    shallow
  );
  const { stats, isLoading, isError } = useStats();

  const displayInCurrency = useCallback(
    (
      amount: number | undefined,
      {
        withHntSuffix,
        needsDivision, // The backend gives values that require division by 100_000_000 to get the amount in HNT
        currency = currentCurrency,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2
      }:
        | {
            withHntSuffix?: boolean;
            needsDivision?: boolean;
            currency?: CurrencyKey;
            minimumFractionDigits?: number;
            maximumFractionDigits?: number;
          }
        | undefined = {
        withHntSuffix: false,
        needsDivision: false,
        currency: currentCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ) => {
      if (!stats || amount == null) return "...";

      const hntAmount = needsDivision ? amount / BONES_PER_HNT : amount;

      if (currency === CurrencyKey.hnt) {
        const displayAmount = hntAmount.toLocaleString("en-US", {
          minimumFractionDigits,
          maximumFractionDigits
        });
        return withHntSuffix ? `${displayAmount}HNT` : displayAmount;
      }

      if ([CurrencyKey.btc, CurrencyKey.eth].includes(currency)) {
        return `${(hntAmount * stats.prices[currency] * 1000).toLocaleString(
          "en-US",
          {
            minimumFractionDigits,
            maximumFractionDigits
          }
        )}m${getCurrencyTitle(currency)}`;
      }

      return (hntAmount * stats.prices[currency]).toLocaleString("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits,
        maximumFractionDigits
      });
    },
    [currentCurrency, getCurrencyTitle, stats]
  );

  return {
    displayInCurrency,
    hntMarket: stats,
    isLoading,
    isError
  };
};

//
// Utils
//

export const BONES_PER_HNT = 100_000_000;
